import { DeleteOutlined } from "@mui/icons-material";
import { Box, FormHelperText } from "@mui/material";
import { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useEffect, useRef, useState } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { FONT_SIZE, NAMED_TIME_RANGE_ROW, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { TimeRangeSelection } from "../../EditTimePopover/EditTimePopover";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { GlyphButton } from "../Clickable/GlyphButton";
import { SystemValidationTextField } from "./SystemValidationTextField";

export interface NamedTimeRangeValue {
	id: string;
	startMinute: number;
	endMinute: number;
	name: string;
}

export interface NamedTimeRangeListCopy {
	startLabel: string;
	endLabel: string;
	nameLabel: string;
	namePlaceholder: string;
	removeLabel: string;
	emptyMessage: string;
	addLabel: string;
	limitLabel: string;
	sameTimeError: string;
	duplicateError: string;
}

const MINUTES_PER_HOUR = 60;
const NAME_MAX_LENGTH = 50;

const toClockMinute = (value: DateTime): number => value.hour * MINUTES_PER_HOUR + value.minute;

/**
 * The range picker edits DateTimes, the list stores clock minutes. Seed both ends on today in the
 * department's timezone and roll an end at/before the start to the next day, so the picker reads a
 * forward span (and its "(8h0m)" duration) for an overnight range like 11 PM–7 AM.
 */
const seedRange = (startMinute: number, endMinute: number, timezone: string): { start: DateTime; end: DateTime } => {
	const day = DateTime.now().setZone(timezone).startOf("day");
	const start = day.set({ hour: Math.floor(startMinute / MINUTES_PER_HOUR), minute: startMinute % MINUTES_PER_HOUR });
	const end = day.set({ hour: Math.floor(endMinute / MINUTES_PER_HOUR), minute: endMinute % MINUTES_PER_HOUR });
	return { start, end: end <= start ? end.plus({ days: 1 }) : end };
};

/** Keep the end's clock time but place it in the 24h after `start` — a new start never shows a negative duration. */
const endAfter = (start: DateTime, end: DateTime): DateTime => {
	const sameDay = start.set({ hour: end.hour, minute: end.minute });
	return sameDay <= start ? sameDay.plus({ days: 1 }) : sameDay;
};

const resolve = (action: SetStateAction<DateTime>, current: DateTime): DateTime => (typeof action === "function" ? action(current) : action);

interface NamedTimeRangeRowProps {
	value: NamedTimeRangeValue;
	onNameChange: (name: string) => void;
	/** Commits a new range; returns false when it would duplicate another row's range. */
	onRangeChange: (startMinute: number, endMinute: number) => boolean;
	onRemove: () => void;
	disabled: boolean;
	dataTestId: string;
	copy: NamedTimeRangeListCopy;
}

/**
 * Internal editable row used by {@link NamedTimeRangeListField}: the system From/To range picker, an
 * optional name and an icon-only remove action. A rejected range (same start and end, or a range another
 * row already has) stays on screen with both fields marked and the reason under them, and is never committed.
 */
export const NamedTimeRangeRow: FC<NamedTimeRangeRowProps> = ({ value, onNameChange, onRangeChange, onRemove, disabled, dataTestId, copy }) => {
	const { timezone } = useHospitalData();
	const [seed] = useState(() => seedRange(value.startMinute, value.endMinute, timezone));
	const [start, setStart] = useState(seed.start);
	const [end, setEnd] = useState(seed.end);
	// The picker fires both setters in one event (its "refresh the other field" hack), so validation reads
	// the latest pair from refs rather than from a render that hasn't happened yet.
	const startRef = useRef(seed.start);
	const endRef = useRef(seed.end);
	// The range this row last handed to the list. `value` lags a render behind a commit, and the picker's
	// second setter call in the same event must not commit the same range again.
	const committedRef = useRef({ startMinute: value.startMinute, endMinute: value.endMinute });
	const [rangeError, setRangeError] = useState<string | null>(null);

	// A range replaced from outside the row (settings applied wholesale) re-seeds the picker. The row's own
	// commits already match, so they fall through.
	useEffect(() => {
		committedRef.current = { startMinute: value.startMinute, endMinute: value.endMinute };
		if (toClockMinute(startRef.current) === value.startMinute && toClockMinute(endRef.current) === value.endMinute) return;
		const next = seedRange(value.startMinute, value.endMinute, timezone);
		startRef.current = next.start;
		endRef.current = next.end;
		setStart(next.start);
		setEnd(next.end);
		setRangeError(null);
	}, [value.startMinute, value.endMinute, timezone]);

	const commitRange = (): void => {
		const startMinute = toClockMinute(startRef.current);
		const endMinute = toClockMinute(endRef.current);
		if (startMinute === endMinute) {
			setRangeError(copy.sameTimeError);
			return;
		}
		const committed = committedRef.current;
		if (startMinute === committed.startMinute && endMinute === committed.endMinute) {
			setRangeError(null);
			return;
		}
		if (!onRangeChange(startMinute, endMinute)) {
			setRangeError(copy.duplicateError);
			return;
		}
		committedRef.current = { startMinute, endMinute };
		setRangeError(null);
	};

	const updateStart: Dispatch<SetStateAction<DateTime>> = (action) => {
		const nextStart = resolve(action, startRef.current);
		startRef.current = nextStart;
		endRef.current = endAfter(nextStart, endRef.current);
		setStart(nextStart);
		setEnd(endRef.current);
		commitRange();
	};

	const updateEnd: Dispatch<SetStateAction<DateTime>> = (action) => {
		endRef.current = resolve(action, endRef.current);
		setEnd(endRef.current);
		commitRange();
	};

	return (
		<Box
			data-testid={dataTestId}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 0.75,
				p: 1,
				borderRadius: 1,
				bgcolor: SURFACE,
				border: `1px solid ${NEUTRAL[200]}`,
			}}
		>
			<Box
				sx={{
					display: "grid",
					alignItems: "center",
					columnGap: 0.5,
					rowGap: 1.25,
					// Narrow: the remove action is the card's top-right corner, so the name gets the card's full width.
					gridTemplateColumns: "minmax(0, 1fr) auto",
					gridTemplateAreas: `"range remove" "name name"`,
					"& > [data-area='remove']": { alignSelf: "start", mt: 1 },
					[`@container (max-width: ${NAMED_TIME_RANGE_ROW.REMOVE_BESIDE_NAME_MAX_WIDTH_PX}px)`]: {
						gridTemplateAreas: `"range range" "name remove"`,
						"& > [data-area='remove']": { alignSelf: "center", mt: 0 },
					},
					[`@container (min-width: ${NAMED_TIME_RANGE_ROW.SINGLE_LINE_MIN_WIDTH_PX}px)`]: {
						gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) auto",
						gridTemplateAreas: `"range name remove"`,
						columnGap: 1,
						"& > [data-area='remove']": { alignSelf: "center", mt: 0 },
					},
				}}
			>
				<Box sx={{ gridArea: "range", minWidth: 0 }}>
					<TimeRangeSelection
						initStartTimeWDefault={seed.start}
						selectedStart={start}
						setSelectedStart={updateStart}
						selectedEnd={end}
						setSelectedEnd={updateEnd}
						disabled={disabled}
						error={rangeError !== null}
						startLabel={copy.startLabel}
						endLabel={copy.endLabel}
						startDataTestId={`${dataTestId}-start`}
						endDataTestId={`${dataTestId}-end`}
						sx={{
							// `&&` outranks the picker's own styled field rules, which would otherwise win on source order. One
							// type size for the row's three fields so "11:00 PM (8h0m)" fits a stacked field in a 280px panel.
							// The same gutter as the rest of the row, so From, To and the name read as evenly spaced fields.
							gap: 1,
							"&& .MuiInputLabel-root, && .MuiInputBase-input": { fontSize: FONT_SIZE.SECONDARY },
							[`@container (max-width: ${NAMED_TIME_RANGE_ROW.STACKED_RANGE_MAX_WIDTH_PX}px)`]: { flexDirection: "column", gap: 1.5 },
						}}
					/>
				</Box>
				<Box sx={{ gridArea: "name", minWidth: 0 }}>
					<SystemValidationTextField
						value={value.name}
						onChange={(event) => onNameChange(event.target.value)}
						label={copy.nameLabel}
						placeholder={copy.namePlaceholder}
						disabled={disabled}
						reserveHeightForHelperText={false}
						inputSx={{ "& .MuiInputBase-root": { minWidth: 0 }, "& .MuiInputBase-input": { fontSize: FONT_SIZE.SECONDARY, minWidth: 0 } }}
						formControlSx={{ minWidth: 0 }}
						labelSx={{ fontSize: FONT_SIZE.SECONDARY }}
						dataTestId={`${dataTestId}-name`}
						inputProps={{ inputProps: { maxLength: NAME_MAX_LENGTH } }}
					/>
				</Box>
				{/* A disabled list is read-only, so it offers no remove action at all. */}
				{!disabled && (
					<InHouseTooltip title={copy.removeLabel}>
						<GlyphButton
							icon={<DeleteOutlined />}
							ariaLabel={copy.removeLabel}
							onClick={onRemove}
							dataTestId={`${dataTestId}-remove`}
							data-area="remove"
							sx={{ gridArea: "remove" }}
						/>
					</InHouseTooltip>
				)}
			</Box>
			{rangeError && (
				<FormHelperText error role="alert" sx={{ m: 0 }}>
					{rangeError}
				</FormHelperText>
			)}
		</Box>
	);
};
